<template>
  <BaseModal
    :dialog.sync="dialog"
    @close="handleClose"
    :title="modalTitle"
    :key="ticketData.idproduto_variacao"
  >
    <v-form @submit.prevent ref="form">
      <v-row>
        <v-col :key="this.ticketData?.variation?.length">
          <TopicTitle
            :icon="'mdi-store'"
            :title="$t('new-event.ticket-management.products.modal.variation')"
            :subtitle="
              $t(
                'new-event.ticket-management.products.modal.variation-description'
              )
            "
          />
          <template v-if="this.ticketData?.variation?.length > 0">
            <template v-for="variation in this.ticketData.variation">
              <v-row
                :key="variation.idvariacao_produto"
                class="d-flex justify-space-between align-center"
              >
                <v-col cols="12" md="2">
                  <BaseTextField
                    :label="
                      $t(
                        'new-event.ticket-management.products.modal.inputs.size'
                      )
                    "
                    v-model="variation.size"
                    :key="ticketData.idproduto_variacao + '-size'"
                    hide-details
                    maxLength="65"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <ColorPicker
                    :label="
                      $t(
                        'new-event.ticket-management.products.modal.inputs.color'
                      )
                    "
                    :edit="variation.color"
                    @updateColor="variation.color = $event"
                    :key="variation.color + ticketData.idproduto_variacao"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <BaseTextField
                    :label="
                      $t(
                        'new-event.ticket-management.products.modal.inputs.color-name'
                      )
                    "
                    v-model="variation.colorName"
                    :rules="[variation.color ? rules.required : false]"
                    hide-spin-buttons
                    :key="ticketData.idproduto_variacao + '-color-name'"
                    hide-details
                    :disabled="!variation.color"
                    maxLength="15"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <BaseTextField
                    :label="
                      $t(
                        'new-event.ticket-management.products.modal.inputs.quantity'
                      )
                    "
                    v-model="variation.quantity"
                    type="number"
                    :rules="[rules.required, rules.nonNegativeNumber]"
                    hide-spin-buttons
                    :key="ticketData.idproduto_variacao + '-quantity'"
                    hide-details
                    @wheel.prevent
                    @focus="$event.target.select()"
                  />
                </v-col>
                <v-col cols="12" md="" class="d-flex align-center">
                  <BaseSelect
                    :items="status"
                    label="
                    Status
                  "
                    v-model="variation.status"
                    item-text="item"
                    item-value="value"
                    :key="ticketData.idproduto_variacao + '-status-variation'"
                    hide-details
                  />
                </v-col>
                <v-col
                  v-if="isAdmin"
                  cols="12"
                  md="1"
                  class="d-flex align-center"
                >
                  <v-btn
                    icon
                    class="primary white--text"
                    width="32"
                    height="32"
                    @click="confirmRemove(variation)"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </template>
          <template v-else>
            <v-row>
              <v-col class="d-flex justify-center align-center" cols="12">
                {{
                  $t("new-event.ticket-management.products.modal.no-variation")
                }}
              </v-col>
            </v-row>
          </template>

          <div
            @click="addVariation()"
            style="cursor: pointer; width: max-content"
            class="d-flex align-center mr-1 pr-1 mt-2"
          >
            <div
              class="d-flex align-center justify-space-between pa-1 rounded-pill primary"
            >
              <v-progress-circular
                v-if="addVariationLoading"
                color="white"
                :size="16"
                indeterminate
                width="2"
              />
              <v-icon v-else size="16" color="white">mdi-plus</v-icon>
            </div>
            <span class="font-weight-semibold primary--text ml-2">{{
              $t("new-event.ticket-management.products.modal.add-variation")
            }}</span>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex flex-column justify-space-between h-100">
          <TopicTitle
            :icon="'mdi-store'"
            :title="
              $t('new-event.ticket-management.products.modal.sales-channel')
            "
            :subtitle="
              $t(
                'new-event.ticket-management.products.modal.sales-channel-description'
              )
            "
          />
          <BaseSelect
            :items="filtredSalesChannel()"
            :label="
              $t(
                'new-event.ticket-management.products.modal.inputs.sales-channel'
              )
            "
            class="self-end"
            v-model="ticketData.salesChannel"
            item-text="value"
            item-value="key"
            :key="ticketData.idproduto_variacao + '-sales-channel'"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-text'"
            :title="$t('new-event.ticket-management.products.modal.desc')"
            :subtitle="
              $t('new-event.ticket-management.products.modal.desc-description')
            "
          />
          <BaseTextarea
            persistent-placeholder
            :label="`${$t('new-event.ticket-management.products.modal.desc')} ${
              selectedLocales.length > 0 ? `(${getTextLocale(locale)})` : ''
            }`"
            v-model="descriptionDefault"
            :key="ticketData.idproduto_variacao + '-description-default-locale'"
            counter="500"
            maxlength="500"
          />
          <v-col
            v-for="(locale, index) in selectedLocales"
            :key="index"
            class="pa-0 ma-0 mt-4"
          >
            <BaseTextarea
              persistent-placeholder
              :label="`${$t(
                'new-event.ticket-management.products.modal.desc'
              )} (${getTextLocale(locale)})`"
              v-model="descriptionLocales[locale]"
              :id="`description-${index}`"
              :key="ticketData.idproduto_variacao + '-description-' + locale"
              counter="500"
              maxlength="500"
            />
          </v-col>
        </v-col>
        <v-col cols="12" md="6">
          <TopicTitle
            :icon="'mdi-ticket-confirmation-outline'"
            :title="
              $t('new-event.ticket-management.tickets.modal.period-access')
            "
            :subtitle="
              $t('new-event.ticket-management.products.modal.check-in-out')
            "
          />
          <DateTimePicker
            :label="
              $t('new-event.ticket-management.tickets.modal.inputs.check-in')
            "
            :edit="ticketData.accessPeriodStart"
            @formatted-date="accessPeriodStartFormattedDate"
            :key="ticketData.accessPeriodStart + 'access-period-start'"
          />
          <div class="mt-4">
            <DateTimePicker
              :label="
                $t('new-event.ticket-management.tickets.modal.inputs.check-out')
              "
              :edit="ticketData.accessPeriodEnd"
              @formatted-date="accessPeriodEndFormattedDate"
              :key="ticketData.accessPeriodEnd + 'access-period-end'"
            />
          </div>
          <BaseTextField
            :label="
              $t('new-event.ticket-management.tickets.modal.inputs.use-limits')
            "
            :hint="
              $t('new-event.ticket-management.tickets.modal.hint-use-limits')
            "
            persistent-hint
            type="number"
            v-model="ticketData.accessQuantity"
            class="mt-4"
            :key="ticketData.idproduto_variacao + '-access-quantity'"
            :rules="[rules.nonNegativeNumber]"
            hide-spin-buttons
            @wheel.prevent
            @focus="$event.target.select()"
          />
        </v-col>
      </v-row>
      <v-row v-if="isAdmin">
        <v-col cols="12" class="pb-0">
          <TopicTitle
            class="mb-4"
            :title="$t('new-event.ticket-management.products.modal.comission')"
            icon="mdi-cash-refund"
            :subtitle="
              $t(
                'new-event.ticket-management.products.modal.comission-description'
              )
            "
          />
        </v-col>
        <v-col sm="6" cols="12" class="pt-0">
          <BaseSelect
            class="mb-4"
            :items="commissionType"
            item-text="item"
            item-value="value"
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.comission'
            )} Web`"
            persistent-placeholder
            v-model="ticketData.commissionWeb"
            :key="ticketData.idproduto_variacao + '-comission-web'"
          ></BaseSelect>
          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <BaseSelect
                item-value="value"
                item-text="item"
                :items="typeTax"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.type-tax'
                )} WEB`"
                persistent-placeholder
                v-model="ticketData.typeTaxWeb"
                :key="ticketData.idproduto_variacao + '-type-tax-web'"
              />
            </v-col>
            <v-col cols="12" md="6">
              <BaseMoneyField
                v-if="ticketData.typeTaxWeb === 'percent'"
                class="mb-4"
                v-model="ticketData.taxWeb"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (%)`"
                persistent-placeholder
                :options="optionsPercent"
                :key="ticketData.idproduto_variacao + '-tax-web'"
                :rules="[rules.maxLimit]"
                selectContent
              />
              <BaseMoneyField
                v-else
                class="mb-4"
                v-model="ticketData.taxWeb"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (R$)`"
                persistent-placeholder
                :options="optionsCurrency"
                :key="ticketData.idproduto_variacao + '-tax-web'"
                selectContent
              />
            </v-col>
          </v-row>
          <BaseMoneyField
            v-model="ticketData.minimumTax"
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.min-tax'
            )} (R$)`"
            persistent-placeholder
            :options="optionsCurrency"
            :key="ticketData.idproduto_variacao + '-min-tax'"
            selectContent
          />
        </v-col>
        <v-col sm="6" cols="12" class="pt-0">
          <BaseSelect
            class="mb-4"
            :items="commissionType"
            :label="`${$t(
              'new-event.ticket-management.tickets.modal.inputs.comission'
            )} POS`"
            persistent-placeholder
            item-text="item"
            item-value="value"
            v-model="ticketData.commissionPos"
            :key="ticketData.idproduto_variacao + '-comission-pos-2'"
          ></BaseSelect>
          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <BaseSelect
                item-text="item"
                item-value="value"
                :items="typeTax"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.type-tax'
                )} POS`"
                persistent-placeholder
                v-model="ticketData.typeTaxPos"
                :key="ticketData.idproduto_variacao + '-type-tax-pos-2'"
              />
            </v-col>
            <v-col cols="12" md="6">
              <BaseMoneyField
                v-if="ticketData.typeTaxPos === 'percent'"
                v-model="ticketData.taxPos"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (%)`"
                persistent-placeholder
                :options="optionsPercent"
                :key="ticketData.idproduto_variacao + '-tax-pos-2'"
                :rules="[rules.maxLimit]"
                selectContent
              />
              <BaseMoneyField
                v-else
                v-model="ticketData.taxPos"
                :label="`${$t(
                  'new-event.ticket-management.tickets.modal.inputs.tax'
                )} (R$)`"
                persistent-placeholder
                :options="optionsCurrency"
                :key="ticketData.idproduto_variacao + '-tax-pos-2'"
                selectContent
              />
            </v-col>
          </v-row>
          <v-col cols="12" class="pl-0 ma-0 d-flex align-center">
            <v-checkbox
              hide-details
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.has-process-tax'
                )
              "
              v-model="ticketData.hasProcessingTax"
              :key="ticketData.idproduto_variacao + 'has-process-tax-2'"
            />
          </v-col>
          <v-col v-if="ticketData.hasProcessingTax" cols="12" class="pl-0">
            <BaseMoneyField
              :options="optionsCurrency"
              v-model="ticketData.processingTax"
              :label="`${$t(
                'new-event.ticket-management.tickets.modal.inputs.process-tax'
              )} (R$)`"
              persistent-placeholder
              :key="ticketData.idproduto_variacao + 'processing-tax-2'"
              selectContent
            />
          </v-col>
        </v-col>
      </v-row>
      <v-row v-if="selectedLocales.length > 0">
        <v-col cols="12">
          <TopicTitle
            :icon="'mdi-translate'"
            :title="$t('new-event.ticket-management.products.modal.translate')"
            :subtitle="
              $t(
                'new-event.ticket-management.products.modal.translate-description'
              )
            "
          />
        </v-col>
        <v-col
          v-for="(locale, index) in selectedLocales"
          :key="index"
          cols="12"
          md="6"
          class="d-flex flex-column justify-end h-100"
        >
          <BaseTextField
            :label="getTextLocale(locale)"
            v-model="nameLocales[locale]"
            :id="`translate-${index}`"
            v-if="nameLocales"
            :key="ticketData.idproduto_variacao + 'translate-' + index"
            maxLength="65"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <TopicTitle
            :icon="'mdi-eye-settings-outline'"
            :title="$t('new-event.ticket-management.tickets.modal.visibility')"
            :subtitle="
              $t(
                'new-event.ticket-management.tickets.modal.visibility-product-description'
              )
            "
          />
          <v-col cols="12" md="6" class="d-flex pa-0 ma-0">
            <v-checkbox
              v-model="ticketData.comboOnly"
              :label="
                $t(
                  'new-event.ticket-management.tickets.modal.inputs.product-combo-only'
                )
              "
              hide-details
              dense
            />
          </v-col>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex justify-end mt-2 flex-wrap">
          <v-btn
            class="rounded-pill primary--text mr-2 mb-2"
            outlined
            @click="handleClose"
            >{{ $t("new-event.buttons.cancel") }}</v-btn
          >

          <v-btn
            class="rounded-pill primary"
            variant="flat"
            @click="handleSave"
            :loading="getModalLoadingEvent"
            >{{ $t("new-event.buttons.confirm-adjusts") }}</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
    <ConfirmAction
      :dialog.sync="confirmDelete"
      :title="$t('common.remove-variation')"
      :subtitle="$t('common.remove-variation-subtitle')"
      @action="deleteVariation"
      @close="cancelRemove"
    />
  </BaseModal>
</template>

<script>
import BaseModal from "@/components/events/EventSettingsModal/BaseModal.vue";
import TopicTitle from "../../TopicTitle.vue";
import BaseSelect from "../../forms/BaseSelect.vue";
import BaseTextField from "../../forms/BaseTextField.vue";
import BaseTextarea from "../../forms/BaseTextarea.vue";
import ColorPicker from "../../ColorPicker.vue";
import DateTimePicker from "../../forms/DateTimePicker.vue";
import BaseMoneyField from "../../forms/BaseMoneyField.vue";
import ConfirmAction from "./ConfirmAction.vue";
import { mapActions, mapGetters } from "vuex";
import {
  getTextLocale,
  processEventName,
  optionsCurrency,
  optionsPercent,
  filtredSalesChannel,
} from "@/utils";

import { createVariationProduct } from "../../../api/newEvent";
import mixins from "@/mixins";
const { formRules } = mixins;

export default {
  components: {
    BaseModal,
    TopicTitle,
    BaseSelect,
    BaseTextField,
    ColorPicker,
    BaseTextarea,
    BaseMoneyField,
    DateTimePicker,
    ConfirmAction,
  },
  mixins: [formRules],
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    eventData: {
      type: Object,
    },
  },
  data() {
    return {
      filtredSalesChannel,
      form: null,
      getTextLocale,
      ticketData: {},
      nameLocales: {},
      descriptionLocales: {},
      descriptionDefault: "",
      optionsCurrency,
      optionsPercent,
      addVariationLoading: false,

      confirmDelete: false,
      variationToDelete: null,
    };
  },
  computed: {
    ...mapGetters([
      "getSelectedLocales",
      "isAdmin",
      "storeSequencial",
      "getIdEvent",
      "getModalLoadingEvent",
    ]),
    modalTitle() {
      const title = this.ticketData.name || this.$t("common.no-product");
      return `
      ${this.$t("new-event.ticket-management.products.modal.title")}
      <span class='primary--text'>${title}</span>
    `;
    },
    selectedLocales() {
      return this.getSelectedLocales;
    },
    locale() {
      return this.$i18n.locale;
    },
    commissionType() {
      return [
        {
          item: this.$t("new-event.settings.comission-type.comission"),
          value: "COMISSAO",
        },
        {
          item: this.$t("new-event.settings.comission-type.convenience"),
          value: "CONVENIENCIA",
        },
      ];
    },
    typeTax() {
      return [
        {
          item: this.$t("new-event.settings.type-tax.percent"),
          value: "percent",
        },
        {
          item: this.$t("new-event.settings.type-tax.value"),
          value: "value",
        },
      ];
    },
    status() {
      return [
        {
          item: this.$t("new-event.settings.status.active"),
          value: "ATIVO",
        },
        {
          item: this.$t("new-event.settings.status.inactive"),
          value: "INATIVO",
        },
        {
          item: this.$t("new-event.settings.status.sold-out"),
          value: "ESGOTADO",
        },
      ];
    },
    formattedEventDescriptions() {
      const defaultDescription = `<${this.locale}>${
        this.descriptionDefault || ""
      }</${this.locale}>`;

      if (this.selectedLocales.length === 0) {
        return defaultDescription;
      }
      const allLocales = this.selectedLocales
        .map(selectedLocale => {
          const description =
            this.descriptionLocales[selectedLocale]?.trim() || "";
          return `<${selectedLocale}>${description}</${selectedLocale}>`;
        })
        .join("");

      return defaultDescription + allLocales;
    },
  },
  methods: {
    ...mapActions([
      "deleteVariationOnProduct",
      "updateEvent",
      "showSnackbarError",
    ]),
    handleClose() {
      this.ticketData = {};
      this.nameLocales = {};
      this.descriptionLocales = {};
      this.descriptionDefault = "";
      this.$emit("close");
    },
    async handleSave() {
      const errors = this.validateVariations();

      if (errors.length > 0) {
        this.showSnackbarError(errors[0]);
        return;
      }
      const data = {
        ...this.ticketData,
        nameLocales: this.nameLocales,
        description: this.formattedEventDescriptions,
        quantity: await this.sumProductQuantity(),
      };
      await this.$emit("update:eventData", data);
      await this.updateEvent();
      this.descriptionLocales = {};
      this.descriptionDefault = "";
    },
    validateVariations() {
      const hasVariation = this.ticketData?.variation?.length > 0;

      if (!hasVariation) return [];

      const hasColor = this.ticketData.variation.some(
        variation => !!variation.color
      );
      const hasSize = this.ticketData.variation.some(
        variation => !!variation.size
      );

      for (let i = 0; i < this.ticketData.variation.length; i++) {
        const variation = this.ticketData.variation[i];

        if (hasColor && !variation.color) {
          return [
            this.$t("new-event.ticket-management.products.modal.error-color"),
          ];
        }

        if (hasSize && !variation.size) {
          return [
            this.$t("new-event.ticket-management.products.modal.error-size"),
          ];
        }
      }

      if (this.ticketData.variation.length === 1) {
        const firstVariation = this.ticketData.variation[0];
        if (!firstVariation.color && !firstVariation.size) {
          return [
            this.$t(
              "new-event.ticket-management.products.modal.error-required"
            ),
          ];
        }
      }

      return [];
    },

    sumProductQuantity() {
      return (
        this.ticketData?.variation?.reduce((sum, variation) => {
          return sum + +variation.quantity;
        }, 0) || 0
      );
    },
    async deleteVariation() {
      try {
        await this.deleteVariationOnProduct({
          eventData: this.eventData,
          variation: this.variationToDelete,
        });
        this.ticketData.variation = this.ticketData?.variation?.filter(
          item =>
            item.idvariacao_produto !==
            this.variationToDelete.idvariacao_produto
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.confirmDelete = false;
        this.variationToDelete = null;
      }
    },
    confirmRemove(variation) {
      this.confirmDelete = true;
      this.variationToDelete = variation;
    },
    cancelRemove() {
      this.confirmDelete = false;
      this.variationToDelete = null;
    },
    async addVariation() {
      this.addVariationLoading = true;
      try {
        await createVariationProduct({
          loja: this.storeSequencial,
          idevent: this.getIdEvent,
          idproduto_variacao: this.ticketData.idproduto_variacao,
        }).then(async response => {
          this.ticketData.variation = [
            ...this.ticketData.variation,
            {
              color: response.data.cor,
              size: response.data.tamanho,
              quantity: response.data.quantidade,
              status: response.data.status,
              idvariacao_produto: response.data.idvariacao_produto,
            },
          ];
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.addVariationLoading = false;
      }
    },
    accessPeriodStartFormattedDate(formatteddatetime) {
      this.ticketData.accessPeriodStart = formatteddatetime;
    },
    accessPeriodEndFormattedDate(formatteddatetime) {
      this.ticketData.accessPeriodEnd = formatteddatetime;
    },
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.ticketData = JSON.parse(JSON.stringify(this.eventData));
        this.nameLocales = { ...this.eventData.nameLocales };
        if (this.ticketData?.description) {
          const descriptions = processEventName(this.ticketData.description);
          this.descriptionDefault = descriptions[this.locale] || "";
          this.descriptionLocales = descriptions;
        }
      } else {
        this.ticketData = {};
      }
    },
  },
};
</script>

<style scoped>
.v-input--selection-controls {
  margin-top: 0px !important;
  padding-top: 0px !important;
}
</style>
